/* The AsyncAPI document, not V3/streamingMarketData.json next to it - that one is the OpenAPI
   spec the Content tab renders. Kept byte-identical to public/V3/streamingMarketData.json, the
   copy served for download, the same way the OpenAPI specs are checked in twice. */
import asyncApiSpec from "../Assets/API/V3/streamingMarketDataAsyncApi.json";

export const START_COMMAND_KEY = "startMarketDataStream";
export const STOP_COMMAND_KEY = "stop";

/* Everything a payload is validated against is read straight out of the checked-in AsyncAPI
   spec, so the Websocket Explorer can never accept something the API definition rendered on
   the other tabs forbids - and cannot drift when the spec is updated. */
const START_SCHEMA = asyncApiSpec?.components?.schemas?.StartMarketData?.properties?.[START_COMMAND_KEY] ?? {};
const REQUIRED_START_FIELDS = START_SCHEMA.required ?? [];

/* MarketDataRequestField, not QuoteType: the spec renamed this schema when "quotes" became
   "fields", and it now carries "tradingEvent" alongside the quote field names. */
export const QUOTE_TYPES = asyncApiSpec?.components?.schemas?.MarketDataRequestField?.enum ?? [];
export const SCHEMES = asyncApiSpec?.components?.schemas?.Scheme?.enum ?? [];
export const QUALITY_OF_SERVICE_TYPES = asyncApiSpec?.components?.schemas?.QualityOfService?.enum ?? [];
export const CONFLATION_TYPES = START_SCHEMA.properties?.conflationType?.enum ?? [];

/* The one conflation type that takes a period. Exported so the form and the validation below
   agree on which selection reveals the period field. */
export const INTERVAL_CONFLATION_TYPE = "INTERVAL";


/* The types the server's own frames carry, read from the spec like everything else here. */
export const START_FRAME_TYPE = asyncApiSpec?.components?.schemas?.MarketDataStart?.properties?.type?.enum?.[0];
export const STOP_FRAME_TYPE = asyncApiSpec?.components?.schemas?.StopAcknowledgment?.properties?.type?.enum?.[0];

/* What a frame means for the set of running streams, or null when it means nothing. Error frames
   reuse the type they reject (Error.type is ["START", "STOP"]), so only "error" separates a
   confirmation from a rejection: going by type alone gave a refused start a chip and took the
   chip from a stream a refused stop had left running. */
export function streamStateChange(frame) {
    const streamId = frame?.streamId;
    if (!streamId || frame.error) return null;

    if (frame.type === START_FRAME_TYPE) return { streamId, running: true };
    if (frame.type === STOP_FRAME_TYPE) return { streamId, running: false };

    return null;
}

const ENUM_FIELDS = [
    { field: "scheme", values: SCHEMES },
    { field: "qualityOfService", values: QUALITY_OF_SERVICE_TYPES },
    { field: "conflationType", values: CONFLATION_TYPES }
];

/* Scheme alone has 26 values, which would bury the message it is appended to, so only lists
   short enough to read at a glance are spelled out. */
function allowedValuesHint(values) {
    return values.length > 0 && values.length <= 8 ? ` Allowed: ${values.join(", ")}.` : "";
}

function invalidEnumReason(start) {
    for (const { field, values } of ENUM_FIELDS) {
        const value = start[field];
        if (value === undefined || values.length === 0 || values.includes(value)) continue;

        return `"${value}" is not a valid ${field}.${allowedValuesHint(values)}`;
    }

    const fields = Array.isArray(start.fields) ? start.fields : [];
    const unknownFields = fields.filter((field) => !QUOTE_TYPES.includes(field));

    return unknownFields.length > 0 ? `Unknown quote type(s): ${unknownFields.join(", ")}.` : null;
}

/* Conflation is checked here, not at the cost of a round trip, on two rules the spec leaves to
   prose. Pairing: the fields look unrelated but the API rejects mismatched pairs (both errors
   listed on the Documentation page), and a period with no type is one of them since the default
   is no conflation. Bounds: conflationPeriod has no enum, only "ISO 8601 duration. Min 100ms,
   max 1 minute" - and the form now allows typing, not just the presets that guaranteed it. */
const MINIMUM_PERIOD_SECONDS = 0.1;
const MAXIMUM_PERIOD_SECONDS = 60;
const ONE_MINUTE_PERIOD = "PT1M";
const PERIOD_IN_SECONDS = /^PT(\d+(?:\.\d+)?)S$/;

function conflationPeriodReason(period) {
    if (period === ONE_MINUTE_PERIOD) return null;

    const secondsMatch = PERIOD_IN_SECONDS.exec(period);
    if (!secondsMatch) {
        return `"${period}" is not an ISO 8601 duration in seconds. Use e.g. "PT30S", or "${ONE_MINUTE_PERIOD}" for a minute.`;
    }

    const seconds = Number(secondsMatch[1]);

    if (seconds < MINIMUM_PERIOD_SECONDS) {
        return `conflationPeriod must be at least 100 ms ("PT${MINIMUM_PERIOD_SECONDS}S").`;
    }

    if (seconds > MAXIMUM_PERIOD_SECONDS) {
        return `conflationPeriod must be at most 1 minute ("${ONE_MINUTE_PERIOD}").`;
    }

    return null;
}

function conflationReason(start) {
    const isInterval = start.conflationType === INTERVAL_CONFLATION_TYPE;
    const hasPeriod = start.conflationPeriod !== undefined && start.conflationPeriod !== "";

    if (isInterval && !hasPeriod) {
        return `"${INTERVAL_CONFLATION_TYPE}" conflation needs a conflationPeriod.`;
    }

    if (!isInterval && hasPeriod) {
        return `conflationPeriod only applies to "${INTERVAL_CONFLATION_TYPE}" conflation.`;
    }

    return hasPeriod ? conflationPeriodReason(start.conflationPeriod) : null;
}

function startCommandReason(start) {
    if (!start || typeof start !== "object" || Array.isArray(start)) {
        return `"${START_COMMAND_KEY}" must be an object.`;
    }

    const missingFields = REQUIRED_START_FIELDS.filter((field) => start[field] === undefined || start[field] === "");
    if (missingFields.length > 0) {
        return `Missing required field(s): ${missingFields.join(", ")}.`;
    }

    const enumReason = invalidEnumReason(start);
    if (enumReason) return enumReason;

    const conflationProblem = conflationReason(start);
    if (conflationProblem) return conflationProblem;

    /* The spec requires at least one field to be requested. */
    const hasFields = Array.isArray(start.fields) && start.fields.length > 0;
    if (!hasFields) {
        return "Add at least one field (a quote type, or \"tradingEvent\").";
    }

    return null;
}

function stopCommandReason(stop) {
    if (!stop || typeof stop !== "object" || Array.isArray(stop)) {
        return `"${STOP_COMMAND_KEY}" must be an object.`;
    }

    return stop.streamId ? null : `"${STOP_COMMAND_KEY}" needs a streamId.`;
}

/* Lives here, and is exported, so the one place that displays it and the one place that
   suppresses the duplicate are talking about the same string. */
export const NO_STREAM_RUNNING_MESSAGE = "No stream is running.";

export function buildStopCommand(streamId) {
    return JSON.stringify({ [STOP_COMMAND_KEY]: { streamId } }, null, 2);
}

/* Reads editor text without logging, so it is safe to run on every keystroke.
   Returns either { command } or { reason }. */
export function readCommand(rawCommand) {
    const trimmedCommand = String(rawCommand ?? "").trim();
    if (!trimmedCommand) return { reason: "The command is empty." };

    let command;
    try {
        command = JSON.parse(trimmedCommand);
    } catch (error) {
        return { reason: `The command must be valid JSON: ${error.message}` };
    }

    if (!command || typeof command !== "object" || Array.isArray(command)) {
        return { reason: "The command must be a JSON object." };
    }

    return { command };
}

export function payloadReason(command) {
    if (STOP_COMMAND_KEY in command) return stopCommandReason(command[STOP_COMMAND_KEY]);
    if (START_COMMAND_KEY in command) return startCommandReason(command[START_COMMAND_KEY]);

    return `The command must be a "${START_COMMAND_KEY}" or "${STOP_COMMAND_KEY}" message.`;
}

/* Why the command in the editor cannot be sent, or null when it can. */
export function editorReason(rawCommand) {
    const { command, reason } = readCommand(rawCommand);
    return reason ?? payloadReason(command);
}

/* The stop editor additionally has to name a stream that is actually running: stopping a
   stream that is not there is the one error the server cannot help us avoid. */
export function stopTargetReason(rawCommand, runningStreamIds) {
    const { command, reason } = readCommand(rawCommand);
    if (reason) return reason;

    const payloadProblem = payloadReason(command);
    if (payloadProblem) return payloadProblem;

    const streamId = command[STOP_COMMAND_KEY]?.streamId;
    if (!streamId) return `The stop command must be a "${STOP_COMMAND_KEY}" message.`;
    if (runningStreamIds.includes(streamId)) return null;

    return runningStreamIds.length === 0
        ? NO_STREAM_RUNNING_MESSAGE
        : `Stream "${streamId}" is not running. Running: ${runningStreamIds.join(", ")}.`;
}
